import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import type { RemoteTrack, Room } from 'livekit-client';
import { VideoLiveStream } from '@shared/models';
import { MibDotsLoaderComponent } from '@medinbox/angular';
import { WaitingVideo } from '../svg';

/** Where the connection stands, and the only thing the waiting screen reads. */
export type LivePlayerStatus = 'connecting' | 'live' | 'ended' | 'error';

/** A track the room is sending us, with the one thing about it the view cares for. */
interface SubscribedTrack {
  readonly track: RemoteTrack;
  /** Kept beside the track so the view can test it without the SDK's enum being loaded. */
  readonly isVideo: boolean;
}

/**
 * How long before the token expires a fresh one is asked for, in milliseconds.
 *
 * The connection in flight outlives its token — LiveKit checks it when joining, not afterwards —
 * so this margin is not about the stream cutting out. It is about the reconnection: a network
 * hiccup an hour after the token died would be refused, and the viewer would be left with a dead
 * player and no way back. Five minutes is enough for the read to fail and be retried.
 */
const RENEW_MARGIN_MS = 5 * 60 * 1000;

/**
 * The floor under the renewal delay, in milliseconds.
 *
 * A token that arrives already expired — a clock skew, an answer read from a stale cache — would
 * otherwise ask for a renewal the instant it lands, and the answer carrying the same date would
 * ask again, and so on. The floor turns that into a slow retry instead of a spin.
 */
const MIN_RENEW_DELAY_MS = 30 * 1000;

/**
 * The live feed of a case, played from LiveKit.
 *
 * Deliberately not a mode of `mib-video-player`: a stream has no past, so no timeline, no resume
 * position and no saved progress.
 *
 * The SDK is loaded on demand, from inside {@link connect}, so the ~1 MB of `livekit-client` is
 * fetched by the viewers who actually open a live and by nobody else. Everything typed from it is
 * imported as a type, which costs nothing at runtime.
 *
 * Only subscribing happens here: the token the backend signs grants `CanSubscribe` and not
 * `CanPublish`, so there is no camera, no microphone and no permission prompt — the appliance is
 * the only publisher in the room.
 */
@Component({
  selector: 'mai-live-player',
  imports: [MibDotsLoaderComponent, WaitingVideo],
  templateUrl: './live-player.html',
  host: {
    class: 'block w-full shrink min-h-0 bg-gray-950 relative',
    '[class.aspect-video]': '!hasVideo()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LivePlayer {
  private readonly destroyRef = inject(DestroyRef);

  /** The credentials of the live, `null` while they are asked for or once nothing is served. */
  readonly live = input.required<VideoLiveStream | null | undefined>();

  /**
   * Asks the host for fresh credentials, the token it holds being about to expire.
   *
   * The player cannot mint a token, so renewing is the host's job: it asks the API again and the
   * fresher `live` object flows back down. Nothing is reconnected on the way — see
   * {@link connectionKey} — so the viewer sees no interruption.
   */
  readonly renewNeeded = output<void>();

  /**
   * The room closed under the player, which is what a stopped live does: the host learns that the
   * live is over without waiting for anything else to tell it.
   *
   * Only for the room in use: closing a connection on purpose — another case, a teardown — says
   * nothing about the live and emits nothing.
   */
  readonly ended = output<void>();

  private readonly liveVideo = viewChild<ElementRef<HTMLVideoElement>>('liveVideo');

  protected readonly status = signal<LivePlayerStatus>('connecting');
  private readonly tracks = signal<SubscribedTrack[]>([]);
  /**
   * Whether the browser is refusing to play sound, which it does until the page has been
   * interacted with. Not an error, and not something to hide: a silent operating room reads as a
   * broken stream, so the view offers the click that lifts it.
   */
  protected readonly audioBlocked = signal(false);

  protected readonly hasVideo = computed(() => this.tracks().some((entry) => entry.isVideo));
  /** The frame the video fills; anything else means there is nothing to show yet. */
  protected readonly playing = computed(() => this.status() === 'live' && this.hasVideo());

  protected readonly waitingText = computed(() => {
    switch (this.status()) {
      case 'connecting':
        return 'Connecting to the live…';
      case 'live':
        return 'Connected. Waiting for the room to send its video…';
      case 'ended':
        return 'The live has ended.';
      case 'error':
        return 'The live could not be joined.';
    }
  });
  /**
   * Whether something is still expected to arrive, which is what the waiting animation claims.
   *
   * Only read while the video is not showing, so `live` here means "joined, and the room has not
   * published yet" — still pending. A live that ended or failed is not, and an animation over
   * either would be telling the viewer to keep waiting for something that is not coming.
   */
  protected readonly pending = computed(
    () => this.status() === 'connecting' || this.status() === 'live',
  );

  /** The room in flight, and the connection it was opened for; both `undefined` when idle. */
  private room?: Room;
  private currentKey?: string;
  private renewTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    /*
     A live is identified by its server and its room, never by its token: a renewed token is the
     same live, and reconnecting on it would black the stream out for no reason. So the key is what
     this effect acts on, and a token that changes under a live connection is simply kept for the
     next reconnection the SDK makes.
    */
    effect(() => {
      const live = this.live();
      untracked(() => this.sync(live));
    });

    effect(() => {
      const expiresAt = this.live()?.expiresAt;
      untracked(() => this.scheduleRenewal(expiresAt));
    });

    /*
     Attaching is driven by the two things it needs rather than done where the track arrives: the
     element and the track appear in either order — a room already publishing hands its tracks over
     within the connection — and a handler firing first would drop them in silence.
    */
    effect(() => {
      const element = this.liveVideo()?.nativeElement;
      const tracks = this.tracks();
      if (element) {
        tracks.forEach((entry) => entry.track.attach(element));
      }
    });

    this.destroyRef.onDestroy(() => {
      clearTimeout(this.renewTimer);
      this.teardown();
    });
  }

  /** Lifts the browser's autoplay block. Only a real user gesture can, hence the button. */
  protected async enableAudio(): Promise<void> {
    await this.room?.startAudio();
  }

  /** Drops the failed connection and opens a new one on the credentials currently held. */
  protected retry(): void {
    this.currentKey = undefined;
    this.sync(this.live());
  }

  /**
   * Opens, keeps or closes the connection to match the credentials on the input.
   *
   * @param live - The credentials last handed over, `null` when the live is over
   */
  private sync(live: VideoLiveStream | null | undefined): void {
    const key = live ? this.connectionKey(live) : undefined;
    if (key === this.currentKey) {
      return;
    }

    this.teardown();
    this.currentKey = key;
    if (live && key) {
      void this.connect(live);
    }
  }

  /** What makes two `live` payloads the same live: the server and the room, and not the token. */
  private connectionKey(live: VideoLiveStream): string {
    return `${live.url}|${live.roomName}`;
  }

  /**
   * Joins the room and subscribes to whatever it publishes.
   *
   * Every handler checks that the room it fires for is still the current one. A viewer navigating
   * between two live cases has the second connection opening while the first is closing, and an
   * event of the old room writing the state of the new one is how a player ends up showing
   * "ended" over a stream that is running.
   *
   * @param live - The credentials to join with
   */
  private async connect(live: VideoLiveStream): Promise<void> {
    this.status.set('connecting');

    const { Room, RoomEvent, Track } = await import('livekit-client');
    // The import is a round trip of its own; the case may have moved on while it was in flight.
    if (this.currentKey !== this.connectionKey(live)) {
      return;
    }

    /*
     `adaptiveStream` lets the SDK ask for the resolution the element actually shows, and pause the
     track entirely while the zone is minimised — which this page does on a click. `dynacast` is
     the publisher's half of the same bargain, harmless here and on by default anyway.
    */
    const room = new Room({ adaptiveStream: true, dynacast: true });
    this.room = room;

    const isCurrent = () => this.room === room;

    room
      .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (isCurrent()) {
          this.tracks.update((tracks) => [
            ...tracks,
            { track, isVideo: track.kind === Track.Kind.Video },
          ]);
        }
      })
      .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach();
        if (isCurrent()) {
          this.tracks.update((tracks) => tracks.filter((entry) => entry.track !== track));
        }
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (isCurrent()) {
          this.audioBlocked.set(!room.canPlaybackAudio);
        }
      })
      .on(RoomEvent.Reconnecting, () => {
        if (isCurrent()) {
          this.status.set('connecting');
        }
      })
      .on(RoomEvent.Reconnected, () => {
        if (isCurrent()) {
          this.status.set('live');
        }
      })
      .on(RoomEvent.Disconnected, () => {
        if (isCurrent()) {
          this.status.set('ended');
          this.tracks.set([]);
          this.ended.emit();
        }
      });

    try {
      await room.connect(live.url, live.token);
      if (!isCurrent()) {
        // Superseded while connecting: this room is nobody's any more, so it is dropped here.
        await room.disconnect();
        return;
      }
      this.status.set('live');
      this.audioBlocked.set(!room.canPlaybackAudio);
    } catch (error) {
      console.error('[LivePlayer] Could not join the live room', live.roomName, error);
      if (isCurrent()) {
        this.status.set('error');
      }
    }
  }

  /** Closes the connection and forgets its tracks, leaving the player ready to open another. */
  private teardown(): void {
    const room = this.room;
    this.room = undefined;
    this.tracks().forEach((entry) => entry.track.detach());
    this.tracks.set([]);
    this.audioBlocked.set(false);
    this.status.set('connecting');
    void room?.disconnect();
  }

  /**
   * Asks for fresh credentials shortly before the token dies, so a later reconnection has a valid one.
   *
   * @param expiresAt - Expiry the payload announced, ISO 8601; `undefined` when there is no live
   */
  private scheduleRenewal(expiresAt: string | undefined): void {
    clearTimeout(this.renewTimer);
    if (!expiresAt) {
      return;
    }

    const expiry = Date.parse(expiresAt);
    if (Number.isNaN(expiry)) {
      return;
    }

    const delay = Math.max(expiry - Date.now() - RENEW_MARGIN_MS, MIN_RENEW_DELAY_MS);
    this.renewTimer = setTimeout(() => this.renewNeeded.emit(), delay);
  }
}
